
import { useState, useEffect } from 'react';
import { supabase } from "@/integrations/supabase/client";
import { OnboardingFormValues } from "../OnboardingFormSchema";
import { UseFormReturn } from "react-hook-form";
import { toast } from "sonner";

interface FormDataItem {
  id: string;
  status: string;
  kol_name: string;
  social_media_handler: string;
  agreed_rate: number;
  currency_type: string;
  start_date: string;
  end_date: string | null;
  pic_id: string;
  social_media_type: string;
  social_media_name: string;
  region: string;
  content_links: string[] | null;
  compensation_amount: number | null;
  compensation_currency: string | null;
  experience_rate: number | null;
  invoice_link: string | null;
  created_at: string;
  updated_at: string;
  status_phase_1?: string;
  status_phase_2?: string;
  status_phase_3?: string;
  status_phase_4?: string;
  status_phase_5?: string;
  status_phase_6?: string;
  status_phase_7?: string;
}

export const useOnboardingFormData = (
  formId: string | undefined,
  form: UseFormReturn<OnboardingFormValues>
) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isExpired, setIsExpired] = useState(false);
  const [formData, setFormData] = useState<FormDataItem | null>(null);

  useEffect(() => {
    const fetchFormData = async () => {
      try {
        if (!formId) {
          // No form ID means this is a new form, not an existing one to edit
          // Initialize the form with default values - especially the PIC ID
          const { data: { user } } = await supabase.auth.getUser();
          if (user) {
            form.setValue('pic_id', user.id);
          }
          
          setIsLoading(false);
          return;
        }

        console.log("Fetching form data for ID:", formId);
        
        // First try to get form by ID
        let { data: formData, error: formError } = await supabase
          .from('onboarding_forms')
          .select('*')
          .eq('id', formId)
          .single();
        
        // If no data found by ID, try to find by unique_link
        if (!formData && formError) {
          console.log("No form found by ID, trying by unique_link");
          // Check if the current URL matches a unique_link
          const currentUrl = window.location.href;
          console.log("Checking if URL matches unique_link:", currentUrl);
          
          const { data: linkData, error: linkError } = await supabase
            .from('onboarding_links')
            .select('form_id')
            .eq('unique_link', currentUrl)
            .maybeSingle();
            
          if (linkData && linkData.form_id) {
            console.log("Found form by unique link:", linkData.form_id);
            const { data: formByLink, error: formByLinkError } = await supabase
              .from('onboarding_forms')
              .select('*')
              .eq('id', linkData.form_id)
              .single();
              
            if (formByLink) {
              formData = formByLink;
              formError = formByLinkError;
            }
          }
        }

        if (!formData || formError) {
          console.error("Error fetching form data:", formError);
          toast.error("Failed to load form data");
          setIsExpired(true);
          setIsLoading(false);
          return;
        }

        const retrievedFormData = formData as FormDataItem;
        setFormData(retrievedFormData);
        console.log("Retrieved form data:", retrievedFormData);

        // Ensure content_links is an array
        const contentLinks = Array.isArray(retrievedFormData.content_links) 
          ? retrievedFormData.content_links 
          : (retrievedFormData.content_links ? [retrievedFormData.content_links] : []);

        // If we get here, the form is valid
        form.reset({
          creator_name: retrievedFormData.kol_name || '',
          social_media_handler: retrievedFormData.social_media_handler || '',
          agreed_rate: retrievedFormData.agreed_rate || 0,
          currency_type: retrievedFormData.currency_type || 'USD',
          start_date: retrievedFormData.start_date ? new Date(retrievedFormData.start_date) : new Date(),
          end_date: retrievedFormData.end_date ? new Date(retrievedFormData.end_date) : undefined,
          pic_id: retrievedFormData.pic_id || '',
          social_media_type: retrievedFormData.social_media_type || '',
          social_media_name: retrievedFormData.social_media_name || '',
          region: retrievedFormData.region || '',
          content_links: contentLinks,
          compensation_amount: retrievedFormData.compensation_amount || undefined,
          compensation_currency: retrievedFormData.compensation_currency || undefined,
          experience_rate: retrievedFormData.experience_rate || undefined,
          invoice_link: retrievedFormData.invoice_link || undefined,
        });
      } catch (error) {
        console.error('Error fetching form data:', error);
        toast.error("Failed to load form data");
        setIsExpired(true);
      } finally {
        setIsLoading(false);
      }
    };

    fetchFormData();
  }, [form, formId]);

  return { isLoading, isExpired, formData };
};
